import React, { useState } from 'react';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { useForm } from 'react-hook-form';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import useFetch from 'use-http';

interface IVendorExcelExportDialogProps {
    dialogName: string;
    hideDialog: () => void;
}

const downloadBlobResponseAsFile = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'document';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

// Port of ap_vendor.fmt's W_Excel window - confirmed via screenshot AND the decoded
// WHEN-BUTTON-PRESSED trigger on the Reports window's own Excel Report button (which just does
// Hide_Window('W_Reports') + Go_Block('Excel') - this is a wholly separate feature from the A/P
// Vendor List report, not "the same report in Excel format"). Oracle's own OK trigger calls
// XL_Ap_Vendors_Paid(beg_date, end_date, hidden, tax_type, ...) to build the file server-side -
// that PL/SQL package was never migrated and isn't decodable from this form, so the backend side
// of this is wired but returns a placeholder file until the real package source is ported.
// The Filename field's file-browse icon in Oracle picked a local save path for its Windows client -
// no web equivalent, so this is just a plain text input feeding the browser's own download.
const VendorExcelExportDialog = (props: IVendorExcelExportDialogProps) => {
    const { register, errors, getValues, trigger } = useForm();
    const { post, response } = useFetch();
    const [loading, setLoading] = useState<boolean>(false);

    const handleClose = () => {
        props.hideDialog();
    };

    const OKClicked = async () => {
        const isValid = await trigger(['fileName', 'beginDate', 'endDate']);
        if (!isValid) {
            return;
        }

        const values = getValues();

        setLoading(true);
        try {
            await post('/v1.0/reports/ap-vendors-paid-excel', {
                beginDate: values.beginDate,
                endDate: values.endDate,
                hidden: values.hidden,
                taxType: values.taxType
            });
            if (response.ok) {
                const blob = await response.blob();
                downloadBlobResponseAsFile(blob, values.fileName?.endsWith('.xlsx') ? values.fileName : `${values.fileName}.xlsx`);
                props.hideDialog();
            } else {
                console.log('Unable to generate A/P Vendors Paid Excel file.');
            }
        } catch (e) {
            console.log('Unable to generate A/P Vendors Paid Excel file.', e);
        }
        setLoading(false);
    };

    return (
        <Dialog title="Excel File" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '30%' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <form style={{ width: '85%' }}>
                    <RHFInputBox
                        label="Beginning Date"
                        labelOnLeft={true}
                        labelStyle={{ width: '10rem' }}
                        name="beginDate"
                        type="date"
                        register={register}
                        errors={errors}
                        rules={{ required: 'Beginning Date is required.' }}
                        style={{ width: '100%' }}
                    />
                    <RHFInputBox
                        label="Ending Date"
                        labelOnLeft={true}
                        labelStyle={{ width: '10rem' }}
                        name="endDate"
                        type="date"
                        register={register}
                        errors={errors}
                        rules={{ required: 'Ending Date is required.' }}
                        style={{ width: '100%' }}
                    />
                    <RHFSelect
                        label="Vendor Type"
                        labelOnLeft={true}
                        labelStyle={{ width: '10rem' }}
                        name="hidden"
                        options={[
                            { value: '', label: 'All Vendors' },
                            { value: 'N', label: 'Not Hidden' },
                            { value: 'Y', label: 'Hidden' },
                        ]}
                        defaultVal=""
                        register={register}
                        errors={errors}
                        style={{ width: '100%' }}
                    />
                    <RHFSelect
                        label="Vendor Tax Type"
                        labelOnLeft={true}
                        labelStyle={{ width: '10rem' }}
                        name="taxType"
                        options={[
                            { value: '', label: 'All' },
                            { value: 'G', label: 'Gross Proceeds Paid to Attorney' },
                            { value: 'C', label: 'Nonemployee Compensation' },
                            { value: 'X', label: 'Not Defined' },
                            { value: 'N', label: 'Not Eligible - Entity Type' },
                            { value: 'O', label: 'Not Eligible - Other' },
                            { value: 'V', label: 'Not Eligible - Vendor Type' },
                            { value: 'I', label: 'Other Income' },
                            { value: 'R', label: 'Rents' },
                        ]}
                        defaultVal=""
                        register={register}
                        errors={errors}
                        style={{ width: '100%' }}
                    />
                    <RHFInputBox
                        label="Filename"
                        labelOnLeft={true}
                        labelStyle={{ width: '10rem' }}
                        name="fileName"
                        type="text"
                        defaultVal="APVendorsPaid.xlsx"
                        register={register}
                        errors={errors}
                        rules={{ required: 'Filename is required.' }}
                        style={{ width: '100%' }}
                    />
                </form>
            </div>
            <div className="form-row" style={{ marginTop: '2rem', justifyContent: 'center' }}>
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={loading ? 'Generating' : 'OK'}
                    disabled={loading}
                    loading={loading}
                    onClick={() => !loading && OKClicked()}
                />
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={'Cancel'}
                    disabled={loading}
                    onClick={() => handleClose()}
                />
            </div>
        </Dialog>
    );
};

export default VendorExcelExportDialog;



// WEBPACK FOOTER //
// ./src/components/accounts-payable/dialogs/VendorExcelExportDialog.tsx